import { z } from 'zod';
/**
 * The environment contract. Both apps validate against this at boot and crash with
 * every problem listed at once, so a misconfigured deploy fails at startup rather
 * than at the first request from a real user.
 *
 * Types are inferred from these schemas and never written beside them — a
 * hand-written interface next to a schema is two sources of truth that drift
 * silently. See spec §13.6.
 */
const url = z.url();
/**
 * Optional, where an empty string counts as absent.
 *
 * A plain `.optional()` accepts `undefined` and rejects `''`, and the difference is
 * not academic: GitHub Actions renders `${{ secrets.NOT_SET }}` as an empty string
 * rather than leaving it unset, and Vite inlines that into the bundle. So a genuinely
 * optional variable crashes the app at boot precisely when nobody configured it.
 *
 * Not hypothetical — the deployed admin threw `EnvValidationError: VITE_SENTRY_DSN`
 * on every page load while CI was green. Compose, `.env` files and shell exports all
 * produce empty strings the same way, so the coercion belongs here.
 */
const optional = (schema) => z.preprocess((value) => (value === '' ? undefined : value), schema.optional());
const commaList = (value) => value
    .split(',')
    .map((item) => item.trim())
    .filter((item) => item.length > 0);
export const apiEnvSchema = z.object({
    NODE_ENV: z
        .enum(['development', 'test', 'production'])
        .default('development'),
    PORT: z.coerce.number().int().positive().default(3000),
    /** Supabase pooled endpoint. Must carry pgbouncer=true&connection_limit=1. */
    DATABASE_URL: z.string().min(1),
    /** Supabase direct endpoint. Migrations need session-level features the pooler drops. */
    DIRECT_URL: z.string().min(1),
    SUPABASE_URL: url,
    SUPABASE_JWKS_URL: url,
    SENTRY_DSN: url,
    /**
     * What Sentry and Grafana file this deploy under — `reviewer` on platform-api,
     * `demo` on the demo API. Not `NODE_ENV`, which is `production` on both.
     */
    SENTRY_ENVIRONMENT: z.preprocess((value) => (value === '' ? undefined : value), z.string().min(1).default('development')),
    OTEL_EXPORTER_OTLP_ENDPOINT: url,
    /** Basic <base64(instanceId:token)>. A wrong encoding is a silent 401. */
    OTEL_EXPORTER_OTLP_HEADERS: z.string().min(1),
    /**
     * Head-sampling ratio, 0–1. Defaults to 1.0: at this traffic level sampling buys
     * nothing, and the variable exists so lowering it later is configuration rather
     * than a code change. Traces containing an error are exported regardless.
     * See `docs/decisions/sampling.md`.
     */
    OTEL_TRACES_SAMPLER_RATIO: z.coerce.number().min(0).max(1).default(1),
    /** Commit SHA. Ties a Sentry issue and a Grafana trace to a line of code. */
    RELEASE_SHA: z.string().min(1),
    /**
     * Where approving a generated scene image files the card asking for a catalog copy —
     * VIS-17. Both optional: without them approval still attaches the image and files
     * nothing. The list id has its own name, not `CLICKUP_LIST_ID`, so a machine holding
     * the repository's ClickUp credentials does not file cards the ship loop then builds.
     */
    CLICKUP_API_TOKEN: optional(z.string().min(1)),
    CATALOG_CARD_LIST_ID: optional(z.string().min(1)),
    /**
     * Server speech — VOICE-02. Both optional: without them Train's speech routes answer
     * 503 and the app offers text. The voices are the account's own, comma-separated; a
     * counterpart keeps one by name.
     */
    ELEVENLABS_API_KEY: optional(z.string().min(1)),
    ELEVENLABS_VOICE_IDS: optional(z.string().transform(commaList).pipe(z.array(z.string()).min(1))),
    /**
     * Lesson pictures — GEN-231. Optional: without it a build writes its lessons with no
     * pictures, so a keyless stack spends nothing.
     */
    GEMINI_API_KEY: optional(z.string().min(1)),
    /**
     * The first local date that counts towards a streak freeze — launch day, so nobody
     * starts with freezes for a run built before they existed. A setting rather than a
     * constant because a local stack needs one weeks back to stage a learner holding two.
     */
    STREAK_FREEZES_FROM: z.iso.date().default('2026-10-07'),
    /**
     * Daily Consultant submissions and custom Train sessions per user — AI-07. Settings so
     * a change is a redeploy, not an app release; zero turns the feature off.
     */
    AI_CONSULTANT_DAILY: z.coerce.number().int().min(0).default(20),
    AI_TRAIN_DAILY: z.coerce.number().int().min(0).default(3),
    /**
     * Comma-separated origins allowed to call this API. The admin back-office is
     * served from a different origin, so an allow-list is required rather than
     * optional. Empty means same-origin only, which is the safe default.
     */
    CORS_ORIGINS: z.string().default('').transform(commaList),
});
/**
 * The deep-link scheme a build falls back to before `ACCT-03b` sets the real one.
 *
 * Exported so `app.config.ts` and this schema cannot disagree about it. They would
 * otherwise hold one default each, and the day they drift the app registers one
 * scheme and asks Supabase to redirect to another.
 */
export const DEFAULT_APP_SCHEME = 'platform';
/** The bundle identifier a build falls back to; `app-identity.md` has it undecided. */
export const DEFAULT_BUNDLE_ID = 'dev.platform.app';
/**
 * What ties a `startdecide.com` share link to the installed app — SHARE-04. The site
 * serves the association files from this and `app.config.ts` declares the same, so the
 * two cannot drift. All of it is public: both files are served to anyone who asks.
 */
export const APP_LINKS = {
    host: 'startdecide.com',
    /** The REL-10 release key every APK is signed with. */
    androidCertSha256: '7F:E5:AE:48:B6:05:58:F6:C3:05:12:C0:42:12:B7:5E:59:4A:93:05:E9:9C:FB:4B:47:D3:BD:0B:1D:77:C8:D4',
    /** Null until the paid Apple membership exists: `docs/decisions/accounts.md`. */
    appleTeamId: null,
};
export const mobileEnvSchema = z.object({
    EXPO_PUBLIC_API_URL: url,
    /** Deep-link scheme. OAuth returns to `<scheme>://auth/callback`. */
    EXPO_PUBLIC_APP_SCHEME: z.string().min(1).default(DEFAULT_APP_SCHEME),
    EXPO_PUBLIC_SUPABASE_URL: url,
    EXPO_PUBLIC_SUPABASE_ANON_KEY: z.string().min(1),
    EXPO_PUBLIC_SENTRY_DSN: url,
    /** Analytics is optional; the app must run without it configured. */
    EXPO_PUBLIC_POSTHOG_KEY: optional(z.string().min(1)),
    EXPO_PUBLIC_POSTHOG_HOST: optional(url),
    /** Session replay; unset, the app records nothing. */
    EXPO_PUBLIC_CLARITY_PROJECT_ID: optional(z.string().min(1)),
    /** Where share links point; a demo build's is the demo site. Unset, `APP_LINKS.host`. */
    EXPO_PUBLIC_WEBSITE_ORIGIN: optional(url),
});
/**
 * The admin back-office. Vite exposes only `VITE_`-prefixed variables to the client
 * bundle, the same guarantee `EXPO_PUBLIC_` gives on mobile — so the prefix is the
 * boundary, and nothing secret may carry it.
 */
export const adminEnvSchema = z.object({
    VITE_API_URL: url,
    VITE_SUPABASE_URL: url,
    VITE_SUPABASE_ANON_KEY: z.string().min(1),
    /** Optional: the admin runs without error reporting configured. */
    VITE_SENTRY_DSN: optional(url),
    /**
     * Where the review viewer is served — spec 013 §10.2. Optional so the console still
     * runs without it: the workspace shows the tree and says the preview is unconfigured,
     * which is more useful than refusing to boot over a panel.
     */
    VITE_VIEWER_URL: optional(url),
});
// No constructor parameter properties: they are TypeScript-only syntax that Node's
// type stripping rejects, which would stop this package being importable without a
// build step. Everything here stays erasable.
export class EnvValidationError extends Error {
    issues;
    constructor(issues, context) {
        const lines = issues.map((i) => `  ${i.path}: ${i.message}`).join('\n');
        super(`Invalid ${context} environment (${String(issues.length)} problem(s)):\n${lines}`);
        this.name = 'EnvValidationError';
        this.issues = issues;
    }
}
/**
 * Parse and validate an environment. Reports *every* problem in one throw rather
 * than failing on the first, so a misconfigured deploy is fixed in one pass instead
 * of one variable per redeploy.
 */
export function parseEnv(schema, source, context) {
    const result = schema.safeParse(source);
    if (result.success)
        return result.data;
    const issues = result.error.issues.map((issue) => ({
        path: issue.path.join('.') || '(root)',
        message: issue.message,
    }));
    throw new EnvValidationError(issues, context);
}
export const parseApiEnv = (source) => parseEnv(apiEnvSchema, source, 'api');
export const parseMobileEnv = (source) => parseEnv(mobileEnvSchema, source, 'mobile');
export const parseAdminEnv = (source) => parseEnv(adminEnvSchema, source, 'admin');
//# sourceMappingURL=env.js.map